import { useState } from 'react';
import { Bookmark, ChevronDown, ChevronUp, Loader2, Power, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SupplyAgentCompletionStrategyPicker } from '@/modules/supply/components/SupplyAgentCompletionStrategyPicker';
import { SupplyAgentReductionStrategyPicker } from '@/modules/supply/components/SupplyAgentReductionStrategyPicker';
import {
  COMPLETION_STRATEGY_OPTIONS,
  REDUCTION_STRATEGY_OPTIONS,
  completionStrategyKey,
  formatSupplyAgentRuleLabel,
  isCompletionRule,
  isReductionRule,
  readCompletionStrategy,
  readReductionStrategy,
  reductionStrategyKey,
  type SupplyAgentCompletionStrategy,
  type SupplyAgentReductionStrategy,
  type SupplyAgentRuleRecord,
} from '@/modules/supply/components/supplyAgentRuleUtils';

interface SupplyAgentSavedRulesListProps {
  rules: SupplyAgentRuleRecord[];
  loading?: boolean;
  compact?: boolean;
  allowDelete?: boolean;
  allowSessionToggle?: boolean;
  allowPersistToggle?: boolean;
  sessionEnabledIds?: Set<number>;
  deletingRuleId?: number | null;
  togglingRuleId?: number | null;
  onDeleteRule?: (ruleId: number) => void | Promise<void>;
  onPurgeRule?: (ruleId: number) => void | Promise<void>;
  onPurgeInactive?: () => void | Promise<void>;
  onSessionToggle?: (ruleId: number, enabled: boolean) => void;
  onPersistToggle?: (ruleId: number, isActive: boolean) => void | Promise<void>;
  orderCompletionStrategy?: SupplyAgentCompletionStrategy | null;
  ruleCompletionStrategies?: Record<string, SupplyAgentCompletionStrategy>;
  onRuleCompletionStrategy?: (rule: SupplyAgentRuleRecord, strategy: SupplyAgentCompletionStrategy) => void;
  completionPickerDisabled?: boolean;
  orderReductionStrategy?: SupplyAgentReductionStrategy | null;
  ruleReductionStrategies?: Record<string, SupplyAgentReductionStrategy>;
  onRuleReductionStrategy?: (rule: SupplyAgentRuleRecord, strategy: SupplyAgentReductionStrategy) => void;
  reductionPickerDisabled?: boolean;
}

function RuleChip({
  rule,
  index,
  allowDelete,
  allowSessionToggle,
  allowPersistToggle,
  sessionEnabledIds,
  deletingRuleId,
  togglingRuleId,
  onDeleteRule,
  onPurgeRule,
  onSessionToggle,
  onPersistToggle,
  t,
  orderCompletionStrategy,
  ruleCompletionStrategies,
  onRuleCompletionStrategy,
  completionPickerDisabled,
  orderReductionStrategy,
  ruleReductionStrategies,
  onRuleReductionStrategy,
  reductionPickerDisabled,
}: {
  rule: SupplyAgentRuleRecord;
  index: number;
  allowDelete: boolean;
  allowSessionToggle: boolean;
  allowPersistToggle: boolean;
  sessionEnabledIds?: Set<number>;
  deletingRuleId: number | null;
  togglingRuleId: number | null;
  onDeleteRule?: (ruleId: number) => void | Promise<void>;
  onPurgeRule?: (ruleId: number) => void | Promise<void>;
  onSessionToggle?: (ruleId: number, enabled: boolean) => void;
  onPersistToggle?: (ruleId: number, isActive: boolean) => void | Promise<void>;
  t: (key: string, opts?: Record<string, unknown>) => string;
  orderCompletionStrategy?: SupplyAgentCompletionStrategy | null;
  ruleCompletionStrategies?: Record<string, SupplyAgentCompletionStrategy>;
  onRuleCompletionStrategy?: (rule: SupplyAgentRuleRecord, strategy: SupplyAgentCompletionStrategy) => void;
  completionPickerDisabled?: boolean;
  orderReductionStrategy?: SupplyAgentReductionStrategy | null;
  ruleReductionStrategies?: Record<string, SupplyAgentReductionStrategy>;
  onRuleReductionStrategy?: (rule: SupplyAgentRuleRecord, strategy: SupplyAgentReductionStrategy) => void;
  reductionPickerDisabled?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const ruleId = rule.id;
  const isDeleting = ruleId != null && deletingRuleId === ruleId;
  const isToggling = ruleId != null && togglingRuleId === ruleId;
  const persistedActive = rule.is_active !== false;
  const sessionOn =
    ruleId == null || !sessionEnabledIds ? persistedActive : sessionEnabledIds.has(ruleId);

  const handleDelete = () => {
    const label = formatSupplyAgentRuleLabel(rule);
    if (!persistedActive) {
      if (!onPurgeRule || ruleId == null) return;
      if (!window.confirm(t('supply.supplyAgent.purgeRuleConfirm', { label }))) return;
      void onPurgeRule(ruleId);
      return;
    }
    if (!allowDelete || !onDeleteRule || ruleId == null) return;
    if (!window.confirm(t('supply.supplyAgent.deleteRuleConfirm', { label }))) return;
    void onDeleteRule(ruleId);
  };

  const canPickCompletion = Boolean(onRuleCompletionStrategy) && isCompletionRule(rule) && persistedActive;
  const canPickReduction = Boolean(onRuleReductionStrategy) && isReductionRule(rule) && persistedActive;
  const canPickStrategy = canPickCompletion || canPickReduction;
  const completionKey = completionStrategyKey(rule);
  const reductionKey = reductionStrategyKey(rule);
  const perRuleCompletion = completionKey ? ruleCompletionStrategies?.[completionKey] : undefined;
  const perRuleReduction = reductionKey ? ruleReductionStrategies?.[reductionKey] : undefined;
  const resolvedCompletion: SupplyAgentCompletionStrategy =
    perRuleCompletion || orderCompletionStrategy || readCompletionStrategy(rule) || 'coverage_layers';
  const resolvedReduction: SupplyAgentReductionStrategy =
    perRuleReduction || orderReductionStrategy || readReductionStrategy(rule) || 'slow_movers';
  const strategyLabel = canPickCompletion
    ? t(
        COMPLETION_STRATEGY_OPTIONS.find((option) => option.id === resolvedCompletion)?.labelKey ||
          'supply.supplyAgent.completionStrategyCoverage',
      )
    : t(
        REDUCTION_STRATEGY_OPTIONS.find((option) => option.id === resolvedReduction)?.labelKey ||
          'supply.supplyAgent.reductionStrategySlowMovers',
      );
  const perRuleBadge = canPickCompletion ? perRuleCompletion : perRuleReduction;

  return (
    <li
      key={String(ruleId ?? `${rule.rule_type}-${index}`)}
      className={canPickStrategy && expanded ? 'flex basis-full flex-col gap-1' : undefined}
    >
      <div
        className={`inline-flex items-center gap-1 rounded-full border pl-2 pr-1 py-0.5 text-[11px] ${
          persistedActive
            ? sessionOn
              ? 'bg-white border-sky-200 text-sky-900'
              : 'bg-white/70 border-sky-100 text-sky-500'
            : 'bg-gray-50 border-gray-200 text-gray-500'
        }`}
        title={
          canPickCompletion
            ? t('supply.supplyAgent.completionStrategyOnRule')
            : canPickReduction
              ? t('supply.supplyAgent.reductionStrategyOnRule')
              : formatSupplyAgentRuleLabel(rule)
        }
      >
        {allowSessionToggle && ruleId != null && persistedActive && onSessionToggle && (
          <input
            type="checkbox"
            className="h-3 w-3 accent-sky-600"
            checked={sessionOn}
            onChange={(e) => onSessionToggle(ruleId, e.target.checked)}
            aria-label={t('supply.supplyAgent.sessionToggle')}
          />
        )}
        {canPickStrategy ? (
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            className={`min-w-0 truncate text-left ${!sessionOn && persistedActive ? 'opacity-70' : ''}`}
          >
            {formatSupplyAgentRuleLabel(rule)}
            {perRuleBadge ? (
              <span
                className={`ml-1 text-[10px] ${canPickCompletion ? 'text-indigo-700' : 'text-amber-800'}`}
              >
                · {strategyLabel}
              </span>
            ) : null}
          </button>
        ) : (
          <span className={!sessionOn && persistedActive ? 'opacity-70' : undefined}>
            {formatSupplyAgentRuleLabel(rule)}
          </span>
        )}
        {allowPersistToggle && ruleId != null && onPersistToggle && (
          <button
            type="button"
            onClick={() => void onPersistToggle(ruleId, !persistedActive)}
            disabled={isToggling || togglingRuleId != null}
            className="inline-flex items-center justify-center rounded-full p-0.5 text-sky-700 hover:bg-sky-100 disabled:opacity-50"
            aria-label={
              persistedActive
                ? t('supply.supplyAgent.deactivateRule')
                : t('supply.supplyAgent.activateRule')
            }
            title={
              persistedActive
                ? t('supply.supplyAgent.deactivateRule')
                : t('supply.supplyAgent.activateRule')
            }
          >
            {isToggling ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Power className={`w-3 h-3 ${persistedActive ? '' : 'opacity-50'}`} />
            )}
          </button>
        )}
        {((allowDelete && persistedActive && onDeleteRule) ||
          (!persistedActive && onPurgeRule)) &&
          ruleId != null && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting || deletingRuleId != null}
            className="inline-flex items-center justify-center rounded-full p-0.5 text-sky-700 hover:bg-sky-100 hover:text-red-700 disabled:opacity-50"
            aria-label={
              persistedActive
                ? t('supply.supplyAgent.deleteRule')
                : t('supply.supplyAgent.purgeRule')
            }
            title={
              persistedActive
                ? t('supply.supplyAgent.deleteRule')
                : t('supply.supplyAgent.purgeRule')
            }
          >
            {isDeleting ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <X className="w-3 h-3" />
            )}
          </button>
        )}
      </div>
      {canPickCompletion && expanded ? (
        <SupplyAgentCompletionStrategyPicker
          value={resolvedCompletion}
          disabled={completionPickerDisabled}
          compact
          onChange={(strategy) => onRuleCompletionStrategy?.(rule, strategy)}
        />
      ) : null}
      {canPickReduction && expanded ? (
        <SupplyAgentReductionStrategyPicker
          value={resolvedReduction}
          disabled={reductionPickerDisabled}
          compact
          onChange={(strategy) => onRuleReductionStrategy?.(rule, strategy)}
        />
      ) : null}
    </li>
  );
}

export function SupplyAgentSavedRulesList({
  rules,
  loading = false,
  compact = false,
  allowDelete = false,
  allowSessionToggle = false,
  allowPersistToggle = false,
  sessionEnabledIds,
  deletingRuleId = null,
  togglingRuleId = null,
  onDeleteRule,
  onPurgeRule,
  onPurgeInactive,
  onSessionToggle,
  onPersistToggle,
  orderCompletionStrategy = null,
  ruleCompletionStrategies,
  onRuleCompletionStrategy,
  completionPickerDisabled = false,
  orderReductionStrategy = null,
  ruleReductionStrategies,
  onRuleReductionStrategy,
  reductionPickerDisabled = false,
}: SupplyAgentSavedRulesListProps) {
  const { t } = useTranslation();
  const [showInactive, setShowInactive] = useState(false);
  const [purgingAll, setPurgingAll] = useState(false);

  if (loading) {
    return (
      <p className="text-xs text-sky-700">
        {t('supply.supplyAgent.loadingSavedRules')}
      </p>
    );
  }

  if (rules.length === 0) {
    return (
      <p className="text-xs text-gray-500">
        {t('supply.supplyAgent.noSavedRules')}
      </p>
    );
  }

  const activeRules = rules.filter((r) => r.is_active !== false);
  const inactiveRules = rules.filter((r) => r.is_active === false);

  return (
    <div
      className={
        compact
          ? 'rounded-md border border-sky-200 bg-sky-50/80 px-2 py-1.5'
          : 'rounded-md border border-sky-200 bg-sky-50 px-3 py-2'
      }
    >
      <p className="text-xs font-medium text-sky-900 flex items-center gap-1 mb-1.5">
        <Bookmark className="w-3.5 h-3.5" />
        {t('supply.supplyAgent.savedRulesActive', { count: activeRules.length })}
      </p>
      {allowSessionToggle && (
        <p className="text-[11px] text-sky-800/80 mb-1.5">
          {t('supply.supplyAgent.sessionToggleHint')}
        </p>
      )}

      {activeRules.length === 0 ? (
        <p className="text-xs text-gray-500 mb-1">
          {t('supply.supplyAgent.noActiveRules')}
        </p>
      ) : (
        <ul className="flex flex-wrap gap-1.5">
          {activeRules.map((rule, index) => (
            <RuleChip
              key={String(rule.id ?? `active-${index}`)}
              rule={rule}
              index={index}
              allowDelete={allowDelete}
              allowSessionToggle={allowSessionToggle}
              allowPersistToggle={allowPersistToggle}
              sessionEnabledIds={sessionEnabledIds}
              deletingRuleId={deletingRuleId}
              togglingRuleId={togglingRuleId}
              onDeleteRule={onDeleteRule}
              onSessionToggle={onSessionToggle}
              onPersistToggle={onPersistToggle}
              orderCompletionStrategy={orderCompletionStrategy}
              ruleCompletionStrategies={ruleCompletionStrategies}
              onRuleCompletionStrategy={onRuleCompletionStrategy}
              completionPickerDisabled={completionPickerDisabled}
              orderReductionStrategy={orderReductionStrategy}
              ruleReductionStrategies={ruleReductionStrategies}
              onRuleReductionStrategy={onRuleReductionStrategy}
              reductionPickerDisabled={reductionPickerDisabled}
              t={t}
            />
          ))}
        </ul>
      )}

      {inactiveRules.length > 0 && allowPersistToggle && (
        <div className="mt-2 pt-1.5 border-t border-sky-100">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowInactive((v) => !v)}
              className="inline-flex items-center gap-1 text-[11px] text-gray-600 hover:text-gray-800"
            >
              {showInactive ? (
                <ChevronUp className="w-3 h-3" />
              ) : (
                <ChevronDown className="w-3 h-3" />
              )}
              {t('supply.supplyAgent.inactiveRulesToggle', { count: inactiveRules.length })}
            </button>
            {onPurgeInactive && (
              <button
                type="button"
                disabled={purgingAll || deletingRuleId != null}
                onClick={() => {
                  if (!window.confirm(t('supply.supplyAgent.purgeInactiveConfirm'))) return;
                  setPurgingAll(true);
                  void Promise.resolve(onPurgeInactive()).finally(() => setPurgingAll(false));
                }}
                className="inline-flex items-center gap-1 text-[11px] text-red-700 hover:text-red-900 disabled:opacity-50"
              >
                {purgingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                {t('supply.supplyAgent.purgeInactive')}
              </button>
            )}
          </div>
          {showInactive && (
            <ul className="mt-1.5 flex flex-wrap gap-1.5">
              {inactiveRules.map((rule, index) => (
                <RuleChip
                  key={String(rule.id ?? `inactive-${index}`)}
                  rule={rule}
                  index={index}
                  allowDelete={false}
                  allowSessionToggle={false}
                  allowPersistToggle={allowPersistToggle}
                  sessionEnabledIds={sessionEnabledIds}
                  deletingRuleId={deletingRuleId}
                  togglingRuleId={togglingRuleId}
                  onPurgeRule={onPurgeRule}
                  onPersistToggle={onPersistToggle}
                  t={t}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
